import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import OrderTableColumnsType from "@/scenes/OrderManagement/enum/OrderTableColumnsType";
import { useOrderManagementStore } from "@/scenes/OrderManagement/store/OrderManagementStore";

export type ResizeHandleSide = "left" | "right";

interface ResizableTableHeadProps {
    columnType: OrderTableColumnsType;

    defaultWidth: number;

    resizeHandleSide?: ResizeHandleSide;

    children: React.ReactNode;
}

const MIN_WIDTH = 150;

const ResizableTableHead: React.FC<ResizableTableHeadProps> = observer(function (props) {
    const { columnType, defaultWidth, resizeHandleSide = "right", children } = props;

    const orderManageStore = useOrderManagementStore();
    const [isResizing, setIsResizing] = useState(false);
    const [startX, setStartX] = useState(0);
    const [startWidth, setStartWidth] = useState(0);
    const thRef = useRef<HTMLTableCellElement>(null);

    const handleResizeStart = (e: React.MouseEvent) => {
        e.preventDefault();
        setIsResizing(true);
        setStartX(e.clientX);
        setStartWidth(thRef.current?.offsetWidth || 0);
    };

    useEffect(() => {
        if (!isResizing) return;

        const getNewWidth = (clientX: number) => {
            const deltaX = clientX - startX;
            const widthDelta = resizeHandleSide === "left" ? -deltaX : deltaX;

            return Math.max(MIN_WIDTH, startWidth + widthDelta);
        };

        const handleMouseMove = (e: MouseEvent) => {
            if (!thRef.current) return;

            const newWidth = getNewWidth(e.clientX);
            const table = thRef.current.closest("table");
            const colgroup = table?.querySelector("colgroup");

            if (colgroup) {
                const cols = colgroup.querySelectorAll("col");
                const thIndex = Array.from(thRef.current.parentElement?.children || []).indexOf(thRef.current);

                if (cols[thIndex]) {
                    (cols[thIndex] as HTMLElement).setAttribute("width", newWidth.toString());
                }
            }

            thRef.current.style.width = newWidth + "px";
            thRef.current.style.minWidth = newWidth + "px";
        };

        const handleMouseUp = (e: MouseEvent) => {
            setIsResizing(false);

            const finalWidth = getNewWidth(e.clientX);
            const previousWidth = orderManageStore.storage.columnWidthMap.get(columnType) || defaultWidth;

            if (finalWidth !== previousWidth) {
                orderManageStore.storage.setColumnWidth(columnType, finalWidth);
            }
        };

        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);

        return () => {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        };
    }, [columnType, defaultWidth, isResizing, orderManageStore, resizeHandleSide, startWidth, startX]);

    const columnWidth = orderManageStore.storage.columnWidthMap.get(columnType) || defaultWidth;
    const isResizeHandleOnLeft = resizeHandleSide === "left";
    const resizeHandle = (
        <div
            onMouseDown={handleResizeStart}
            style={{
                width: "4px",
                height: "20px",
                cursor: "col-resize",
                backgroundColor: isResizing ? "#1890ff" : "#e8e8e8",
                borderRadius: "2px",
                transition: "background-color 0.2s",
                marginLeft: isResizeHandleOnLeft ? 0 : "4px",
                marginRight: isResizeHandleOnLeft ? "4px" : 0,
                flexShrink: 0,
            }}
            onMouseEnter={(e) => {
                if (!isResizing) {
                    (e.currentTarget as HTMLElement).style.backgroundColor = "#1890ff";
                }
            }}
            onMouseLeave={(e) => {
                if (!isResizing) {
                    (e.currentTarget as HTMLElement).style.backgroundColor = "#e8e8e8";
                }
            }}
        />
    );

    return (
        <th
            ref={thRef}
            style={{
                width: columnWidth,
                position: "relative",
                userSelect: isResizing ? "none" : "auto",
                whiteSpace: "nowrap",
            }}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: isResizeHandleOnLeft ? "flex-start" : "space-between",
                    alignItems: "center",
                    paddingLeft: 0,
                    paddingRight: isResizeHandleOnLeft ? 0 : "8px",
                }}
            >
                {isResizeHandleOnLeft ? resizeHandle : null}
                {children}
                {isResizeHandleOnLeft ? null : resizeHandle}
            </div>
        </th>
    );
});

export default ResizableTableHead;
